import { Link } from 'react-router';
import type { OrderSummary } from '@gatecheckpro/contracts';
import {
  EmptyState,
  LoadingRegion,
  ORDER_STATES,
  Status,
  formatCountdown,
  formatMoney,
  hasLapsed,
  presentation,
} from '@gatecheckpro/design-system';
import { Chrome } from './Chrome.tsx';
import { useResource } from '../lib/useResource.ts';
import { useNowTicking } from '../lib/useCountdown.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * A customer's orders.
 *
 * Deliberately not called "tickets". No ticket has been issued — these are
 * reservations and payment obligations, and conflating the two is the mistake
 * this product exists to avoid.
 */
export function OrdersPage() {
  const { resource } = useResource<{ orders: OrderSummary[] }>('/api/v1/customer/orders');
  const now = useNowTicking();

  return (
    <Chrome>
      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose" style={{ maxWidth: '46rem' }}>
          <div className="gc-page-header">
            <span className="gc-eyebrow">Your account</span>
            <h1>Orders</h1>
            <p className="gc-muted">
              Reservations and what you owe on them. Tickets themselves arrive once payment works.
            </p>
          </div>

          {resource.status === 'loading' && <LoadingRegion label="Loading your orders" />}
          {resource.status === 'failed' && <FailureNotice error={resource.error} />}

          {resource.status === 'loaded' &&
            (resource.data.orders.length === 0 ? (
              <EmptyState
                title="You have not reserved anything yet"
                action={
                  <Link to="/" className="gc-button gc-button--primary">
                    See what&rsquo;s on
                  </Link>
                }
              >
                When you reserve tickets they appear here, with the exact price you were quoted.
              </EmptyState>
            ) : (
              <div className="gc-stack">
                {resource.data.orders.map((order) => {
                  const state = presentation(ORDER_STATES, order.status);
                  const holding =
                    order.holdExpiresAt !== null && !hasLapsed(order.holdExpiresAt, now);
                  return (
                    <Link
                      key={order.orderId}
                      to={`/orders/${order.orderId}`}
                      className="gc-event-card"
                    >
                      <div className="gc-row gc-row--between">
                        <span className="gc-event-card__title">{order.eventTitle}</span>
                        <Status
                          tone={state.tone}
                          glyph={state.glyph}
                          label={state.label}
                          title={state.meaning}
                        />
                      </div>
                      <span className="gc-muted gc-mono">{order.orderNumber}</span>
                      <div className="gc-row gc-row--between">
                        <span className="gc-summary__value">{formatMoney(order.amount)}</span>
                        {holding && (
                          <span className="gc-muted">
                            held for {formatCountdown(order.holdExpiresAt as string, now)}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            ))}
        </div>
      </main>
    </Chrome>
  );
}
